import { client } from '@/client';
import { useNotifications } from '@/hooks/notifications.hook';
import { ToastType, useToast } from '@/hooks/useToast';
import React, { createContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

type CampaignLaunchState = {
  setLaunchedCampaignId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  isConnected: React.MutableRefObject<boolean>;
};

export const CampaignLaunchContext = createContext<CampaignLaunchState>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setLaunchedCampaignId: () => {},
  isConnected: { current: false },
});

export const CampaignLaunchNotify: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const {
    lastMessage,
    setNotificationParam: setLaunchedCampaignId,
    notificationParam: launchedCampaignId,
    isConnected,
  } = useNotifications(client.campaigns.campaignsNotifyUrl);

  useEffect(() => {
    if (!launchedCampaignId) return;
    handleNotifyCampaignLaunch(lastMessage);
  }, [JSON.stringify(lastMessage)]);

  const handleNotifyCampaignLaunch = (messages: any) => {
    if (
      !messages?.data?.event ||
      ![
        'campaign.create.sync.finished',
        'campaign.create.sync.failed',
      ].includes(messages?.data?.event)
    )
      return;

    const statuses: {
      [key: string]: [ToastType | undefined, string, string];
    } = {
      'campaign.create.sync.finished': [
        toast?.success,
        'toast.success',
        'campaign.success.launch',
      ],
      'campaign.create.sync.failed': [
        toast?.info,
        'toast.info',
        'campaign.fail.launch',
      ],
    };

    const [callback, title, description] = statuses[messages?.data?.event];

    callback && callback(t(title), t(description));

    queryClient.invalidateQueries(['campaigns']);
    setLaunchedCampaignId(undefined);
  };

  return (
    <CampaignLaunchContext.Provider
      value={{
        setLaunchedCampaignId,
        isConnected,
      }}
    >
      {children}
    </CampaignLaunchContext.Provider>
  );
};
